import { useAuthStore } from '@/shared/stores/auth'
import { capturePresentationOwner } from './i18n/chatPresentationOwner'
import { apiErrorBody, apiErrorStatus, type AdminResetResult } from './api'
import {
  RESET_EXECUTION_PATH,
  RESET_REAUTH_ATTEMPT_PATH,
  resetExecutionInputSchema,
  resetReauthAttemptSchema,
  resetOperationIdSchema,
  resetOperationStatusSchema,
} from './reset-contract'

type ResetSender = <T>(
  method: string,
  path: string,
  body?: unknown,
  timeout?: number,
  headers?: Record<string, string>,
  owner?: { tenantId: string; userId: string },
  beforeSend?: () => void,
) => Promise<T>

function currentOwner() {
  const { tenantId, userId } = useAuthStore.getState()
  if (!tenantId || !userId) throw new Error('Reset requires an authenticated owner')
  return { tenantId, userId }
}

/** Owner-fenced nonce exchange; checking an existing operation never submits another reset. */
export function createResetExecutionApi(send: ResetSender) {
  return {
    resetTotal: async (password: string, operationId: string = crypto.randomUUID()) => {
      const owner = currentOwner()
      const ensureOwner = capturePresentationOwner(owner)
      const id = resetOperationIdSchema.parse(operationId)
      let sent = false
      try {
        const attempt = resetReauthAttemptSchema.parse(
          await send<unknown>('POST', RESET_REAUTH_ATTEMPT_PATH, {}, undefined, undefined, owner),
        )
        ensureOwner()
        const result = await send<AdminResetResult>(
          'POST',
          RESET_EXECUTION_PATH,
          resetExecutionInputSchema.parse({ password, attemptId: attempt.attemptId }),
          180_000,
          { 'X-Request-Id': id },
          owner,
          () => {
            sent = true
          },
        )
        ensureOwner()
        if (result?.success !== true) throw new Error('Reset success was not confirmed')
        return result
      } catch (cause) {
        if (!sent) throw new ResetExecutionNotStarted(cause)
        throw cause
      }
    },
    resetTotalStatus: async (operationId: string) => {
      const owner = currentOwner()
      const id = resetOperationIdSchema.parse(operationId)
      const ensureOwner = capturePresentationOwner(owner)
      const result = await send<unknown>(
        'GET',
        `${RESET_EXECUTION_PATH}/status?operationId=${encodeURIComponent(id)}`,
        undefined,
        undefined,
        undefined,
        owner,
      )
      ensureOwner()
      return resetOperationStatusSchema.parse(result)
    },
  }
}

/** Local transport proof, not a server claim: the destructive fetch was never submitted. */
export class ResetExecutionNotStarted extends Error {
  constructor(cause: unknown) {
    super('Reset execution was not sent', { cause })
  }
  get status() {
    return apiErrorStatus(this.cause)
  }
  get body() {
    return apiErrorBody(this.cause)
  }
}
